import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Trash2, Mail, Check, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  general:       { label: "General",       color: "#a3a3a3" },
  casting:       { label: "Casting",       color: "#c01515" },
  press:         { label: "Press",         color: "#d4a017" },
  collaboration: { label: "Collab",        color: "#4a90c2" },
};

const STATUS_OPTIONS = [
  { value: "pending",     label: "Pending"     },
  { value: "in_progress", label: "In progress" },
  { value: "replied",     label: "Replied"     },
  { value: "closed",      label: "Closed"      },
];

const CATEGORY_FILTERS = ["all", "general", "casting", "press", "collaboration"];

export default function AdminContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, by_category: {}, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");

  // editor local state
  const [notesDraft, setNotesDraft] = useState("");
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`,    { withCredentials: true }),
      ]);
      setContacts(c.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Could not load inbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selected = useMemo(
    () => contacts.find((c) => c.id === selectedId) || null,
    [contacts, selectedId],
  );

  useEffect(() => {
    setNotesDraft(selected?.notes || "");
    setReplyOpen(false);
    setReplySubject(selected ? `Re: your note (${CATEGORY_META[selected.category || "general"].label})` : "");
    setReplyBody("");
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredContacts = useMemo(() => {
    if (filter === "all") return contacts;
    return contacts.filter((c) => (c.category || "general") === filter);
  }, [contacts, filter]);

  const cat = (c) => c.category || "general";

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
    setSelectedId(null);
    toast.success("Submission deleted");
    fetchData();
  };

  const handleMarkRead = async (id) => {
    await axios.patch(`${API}/admin/contacts/${id}/read`, {}, { withCredentials: true });
    fetchData();
  };

  const handleSetStatus = async (id, status) => {
    await axios.patch(`${API}/admin/contacts/${id}/status`, { status }, { withCredentials: true });
    toast.success(`Status → ${status}`);
    fetchData();
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    await axios.patch(`${API}/admin/contacts/${selected.id}/notes`,
      { notes: notesDraft }, { withCredentials: true });
    toast.success("Notes saved");
    fetchData();
  };

  const handleSendReply = async () => {
    if (!selected) return;
    if (!replySubject.trim() || !replyBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    try {
      setReplying(true);
      await axios.post(`${API}/admin/contacts/${selected.id}/reply`,
        { subject: replySubject, body: replyBody },
        { withCredentials: true });
      toast.success("Reply sent");
      setReplyOpen(false);
      setReplyBody("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally {
      setReplying(false);
    }
  };

  return (
    <div data-testid="admin-contacts-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-10">
        <div className="border border-white/10 p-5 col-span-2 md:col-span-2">
          <p className="overline mb-3">Total Submissions</p>
          <p className="font-display font-light" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
             data-testid="stat-total">
            {String(stats.total).padStart(2, "0")}
          </p>
        </div>
        <div className="border border-white/10 p-5">
          <p className="overline mb-3">Unread</p>
          <p className="font-display font-light text-[#c01515]" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}
             data-testid="stat-unread">
            {String(stats.unread).padStart(2, "0")}
          </p>
        </div>
        {["casting", "press", "collaboration"].map((k) => (
          <div key={k} className="border border-white/10 p-5">
            <p className="overline mb-3" style={{ color: CATEGORY_META[k].color }}>
              ● {CATEGORY_META[k].label}
            </p>
            <p className="font-display font-light" style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.8rem)" }}
               data-testid={`stat-cat-${k}`}>
              {String(stats.by_category?.[k] || 0).padStart(2, "0")}
            </p>
          </div>
        ))}
      </div>

      {/* Category filter */}
      <div className="flex gap-2 flex-wrap mb-6" data-testid="category-filter">
        {CATEGORY_FILTERS.map((k) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`font-mono text-[10px] tracking-[0.25em] uppercase border px-4 py-2 transition-all ${
              filter === k
                ? "border-[#c01515] text-[#c01515] bg-[#c01515]/10"
                : "border-white/15 text-[#a3a3a3] hover:text-white hover:border-white/40"
            }`}
          >
            {k === "all" ? "All" : CATEGORY_META[k].label}
            {k !== "all" && stats.by_category?.[k] != null && (
              <span className="ml-2 opacity-60">
                {String(stats.by_category[k]).padStart(2, "0")}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Inbox */}
        <div className="lg:col-span-5 border border-white/10">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <span className="overline">Inbox</span>
            <span className="overline">{filteredContacts.length} entries</span>
          </div>
          {loading ? (
            <div className="p-10 text-center overline" data-testid="loading">Loading…</div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-10 text-center overline" data-testid="empty-state">
              No dossiers in this category yet.
            </div>
          ) : (
            <ul className="divide-y divide-white/10 max-h-[70vh] overflow-y-auto">
              {filteredContacts.map((c) => {
                const meta = CATEGORY_META[cat(c)];
                return (
                  <li
                    key={c.id}
                    data-testid={`contact-row-${c.id}`}
                    onClick={() => setSelectedId(c.id)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedId === c.id ? "bg-white/5" : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {!c.read && <span className="w-1.5 h-1.5 rounded-full bg-[#c01515]" />}
                          <p className="font-display text-lg truncate">{c.name}</p>
                          <span
                            className="font-mono text-[9px] tracking-[0.25em] uppercase border px-2 py-0.5"
                            style={{ borderColor: meta.color, color: meta.color }}
                          >{meta.label}</span>
                          <span className="font-mono text-[9px] tracking-[0.25em] uppercase opacity-60">
                            · {(c.status || "pending").replace("_", " ")}
                          </span>
                        </div>
                        <p className="overline truncate">{c.email}</p>
                        <p className="text-sm text-[#a3a3a3] mt-2 line-clamp-2">{c.message}</p>
                      </div>
                      <span className="overline whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 border border-white/10 min-h-[60vh]">
          {selected ? (
            <div className="p-8 md:p-10" data-testid="contact-detail-panel">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="overline">
                    Received · {new Date(selected.created_at).toLocaleString()}
                  </span>
                  <span
                    className="font-mono text-[9px] tracking-[0.25em] uppercase border px-2 py-0.5"
                    style={{
                      borderColor: CATEGORY_META[cat(selected)].color,
                      color: CATEGORY_META[cat(selected)].color,
                    }}
                  >{CATEGORY_META[cat(selected)].label}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {!selected.read && (
                    <button
                      data-testid="mark-read-btn"
                      onClick={() => handleMarkRead(selected.id)}
                      className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-3 py-2 hover:bg-white hover:text-black transition-all flex items-center gap-2"
                    ><Check size={12} /> Mark read</button>
                  )}
                  <button
                    data-testid="reply-btn"
                    onClick={() => setReplyOpen((v) => !v)}
                    className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-3 py-2 hover:bg-white hover:text-black transition-all flex items-center gap-2"
                  ><Send size={12} /> {replyOpen ? "Cancel" : "Reply"}</button>
                  <button
                    data-testid="delete-contact-btn"
                    onClick={() => handleDelete(selected.id)}
                    className="font-mono text-[10px] tracking-[0.25em] uppercase border border-[#c01515]/40 text-[#c01515] px-3 py-2 hover:bg-[#c01515] hover:text-white transition-all flex items-center gap-2"
                  ><Trash2 size={12} /> Delete</button>
                </div>
              </div>

              <h2 className="font-display font-light leading-tight tracking-[-0.02em] mb-3"
                  style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                {selected.name}
              </h2>
              <a href={`mailto:${selected.email}`}
                 className="overline text-[#c01515] inline-flex items-center gap-2">
                <Mail size={12} />{selected.email}
              </a>
              {selected.phone && (
                <a href={`tel:${selected.phone}`} data-testid="contact-detail-phone"
                   className="overline text-[#a3a3a3] hover:text-white inline-flex items-center gap-2 ml-6">
                  <span className="font-mono">☎</span>{selected.phone}
                </a>
              )}
              <div className="dotted-line my-8" />
              <p className="font-display text-2xl md:text-3xl leading-snug whitespace-pre-wrap"
                 data-testid="contact-detail-message">
                {selected.message}
              </p>

              {/* Status & Notes */}
              <div className="dotted-line my-8" />
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                <div className="md:col-span-4">
                  <p className="overline mb-3">Status</p>
                  <div className="flex flex-wrap gap-2" data-testid="status-buttons">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s.value}
                        data-testid={`status-${s.value}`}
                        onClick={() => handleSetStatus(selected.id, s.value)}
                        className={`font-mono text-[10px] tracking-[0.25em] uppercase border px-3 py-2 transition-all ${
                          (selected.status || "pending") === s.value
                            ? "border-[#c01515] text-[#c01515] bg-[#c01515]/10"
                            : "border-white/15 text-[#a3a3a3] hover:text-white hover:border-white/40"
                        }`}
                      >{s.label}</button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="flex items-center justify-between mb-3">
                    <p className="overline">Internal Notes</p>
                    <button
                      data-testid="save-notes-btn"
                      onClick={handleSaveNotes}
                      className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-3 py-2 hover:bg-white hover:text-black transition-all"
                    >Save notes</button>
                  </div>
                  <textarea
                    data-testid="notes-textarea"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={5}
                    placeholder="Private notes — only visible in The Vault."
                    className="w-full bg-[#080808] border border-white/15 text-[#f5f5f5] p-3 font-mono text-sm focus:outline-none focus:border-white/40"
                  />
                </div>
              </div>

              {/* Reply composer */}
              {replyOpen && (
                <div className="mt-8 border border-white/15 p-5" data-testid="reply-composer">
                  <p className="overline mb-3">Compose reply → {selected.email}</p>
                  <input
                    data-testid="reply-subject"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Subject"
                    className="w-full bg-[#080808] border border-white/15 text-[#f5f5f5] p-3 font-mono text-sm mb-3 focus:outline-none focus:border-white/40"
                  />
                  <textarea
                    data-testid="reply-body"
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    rows={8}
                    placeholder="Write your reply…"
                    className="w-full bg-[#080808] border border-white/15 text-[#f5f5f5] p-3 font-display text-base focus:outline-none focus:border-white/40"
                  />
                  <div className="flex justify-end mt-3 gap-2">
                    <button
                      onClick={() => setReplyOpen(false)}
                      className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 px-4 py-2"
                    >Cancel</button>
                    <button
                      data-testid="send-reply-btn"
                      onClick={handleSendReply}
                      disabled={replying}
                      className="font-mono text-[10px] tracking-[0.25em] uppercase border border-[#c01515] bg-[#c01515] text-white px-4 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    ><Send size={12} /> {replying ? "Sending…" : "Send reply"}</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-10">
              <p className="overline text-center">Select a dossier from the inbox →</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
